import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDesktop, faPlug, faCheckCircle, faBan, faFileAlt, faShieldAlt,
  faPlus, faXmark, faGlobe, faToggleOn, faToggleOff, faLocationDot,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../components/shared/DataTable';
import { UiPageHeader, UiInput, StatusBadge } from '../components/ui';
import type { StatusDef } from '../components/ui';

interface AccessLog {
  id: string;
  ip_address: string;
  access_type: 'portal' | 'api';
  allowed: boolean;
  endpoint?: string;
  created_at: string;
}

const LOG_STATUS_MAP: Record<string, StatusDef> = {
  allowed: { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: 'Allowed' },
  blocked: { bg: 'bg-red-100', text: 'text-red-600', icon: faBan, label: 'Blocked' },
};

const IPWhitelistPage: React.FC = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState<any>({
    portal_ips: [],
    api_ips: [],
    enabled: false,
    current_ip: '',
  });
  const [newPortalIP, setNewPortalIP] = useState('');
  const [newAPIIP, setNewAPIIP] = useState('');
  const [logs, setLogs] = useState<AccessLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await merchantApi.get('/ip-whitelist');
      setConfig(response.data.data);
    } catch (error: any) {
      toast.error(t('common.cannotLoadData'));
    }
  }, []);

  const loadLogs = useCallback(async () => {
    try {
      setLoadingLogs(true);
      const response = await merchantApi.get('/ip-whitelist/logs?limit=20');
      setLogs(response.data.data || []);
    } catch (error) {
      console.error('Error loading logs');
    } finally {
      setLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadLogs();
  }, [loadConfig, loadLogs]);

  const handleToggle = async () => {
    try {
      await merchantApi.put('/ip-whitelist', { enabled: !config.enabled });
      toast.success(config.enabled ? t('ipWhitelist.disabled') : t('ipWhitelist.enabled'));
      loadConfig();
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const handleAddPortalIP = async () => {
    if (!newPortalIP) return;
    try {
      await merchantApi.put('/ip-whitelist', { portal_ips: [...config.portal_ips, newPortalIP] });
      toast.success(t('ipWhitelist.addSuccess'));
      setNewPortalIP('');
      loadConfig();
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const handleAddAPIIP = async () => {
    if (!newAPIIP) return;
    try {
      await merchantApi.put('/ip-whitelist', { api_ips: [...config.api_ips, newAPIIP] });
      toast.success(t('ipWhitelist.addSuccess'));
      setNewAPIIP('');
      loadConfig();
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const handleRemovePortalIP = async (ip: string) => {
    try {
      await merchantApi.put('/ip-whitelist', { portal_ips: config.portal_ips.filter((i: string) => i !== ip) });
      toast.success(t('ipWhitelist.removeSuccess'));
      loadConfig();
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const handleRemoveAPIIP = async (ip: string) => {
    try {
      await merchantApi.put('/ip-whitelist', { api_ips: config.api_ips.filter((i: string) => i !== ip) });
      toast.success(t('ipWhitelist.removeSuccess'));
      loadConfig();
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  // Columns for Access Logs DataTable
  const logColumns: Column<AccessLog>[] = [
    {
      key: 'ip_address',
      header: 'IP Address',
      sortable: true,
      render: (log) => (
        <code className="text-gray-800 dark:text-slate-100 font-mono bg-gray-100 dark:bg-slate-700 px-2 py-1 rounded">{log.ip_address}</code>
      ),
    },
    {
      key: 'access_type',
      header: 'Type',
      sortable: true,
      render: (log) => (
        log.access_type === 'portal' ? (
          <span className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 font-medium">
            <FontAwesomeIcon icon={faDesktop} /> Portal
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-purple-600 dark:text-purple-400 font-medium">
            <FontAwesomeIcon icon={faPlug} /> API
          </span>
        )
      ),
    },
    {
      key: 'allowed',
      header: 'Status',
      sortable: true,
      render: (log) => <StatusBadge status={log.allowed ? 'allowed' : 'blocked'} statusMap={LOG_STATUS_MAP} />,
    },
    {
      key: 'endpoint',
      header: 'Endpoint',
      render: (log) => (
        <span className="text-sm text-gray-500 dark:text-slate-400 font-mono">{log.endpoint || '-'}</span>
      ),
    },
    {
      key: 'created_at',
      header: 'Time',
      sortable: true,
      render: (log) => (
        <span className="text-sm text-gray-500 dark:text-slate-400">
          {new Date(log.created_at).toLocaleString('th-TH')}
        </span>
      ),
    },
  ];

  const renderIpList = (ips: string[], onRemove: (ip: string) => void) => (
    <div className="space-y-2 max-h-64 overflow-y-auto">
      {ips.length === 0 ? (
        <p className="text-center text-gray-400 dark:text-slate-500 py-4">{t('ipWhitelist.noIPs')}</p>
      ) : (
        ips.map((ip) => (
          <div key={ip} className="flex items-center justify-between bg-gray-50 dark:bg-slate-700/50 rounded-lg px-4 py-3">
            <code className="text-gray-800 dark:text-slate-100 font-mono">{ip}</code>
            <button
              onClick={() => onRemove(ip)}
              className="text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors p-1"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="w-full">
      <UiPageHeader icon={faShieldAlt} title="IP Whitelist" subtitle={t('ipWhitelist.desc')} />

      {/* Current IP */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme p-5 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faLocationDot} className="text-xl" style={{ color: 'var(--theme-primary)' }} />
          <div>
            <p className="text-sm text-gray-500 dark:text-slate-400">Your Current IP</p>
            <code className="text-lg font-bold text-gray-800 dark:text-slate-100">{config.current_ip}</code>
          </div>
        </div>
      </div>

      {/* Toggle Section */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-theme p-5 mb-6 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-800 dark:text-slate-100">{t('ipWhitelist.toggleTitle')}</h3>
          <p className="text-sm text-gray-500 dark:text-slate-400">{t('ipWhitelist.toggleDesc')}</p>
        </div>
        <button
          onClick={handleToggle}
          className={`text-4xl transition-colors ${config.enabled ? 'text-green-500' : 'text-gray-300 dark:text-slate-600'}`}
        >
          <FontAwesomeIcon icon={config.enabled ? faToggleOn : faToggleOff} />
        </button>
      </div>

      {/* Whitelist Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Portal IPs */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <FontAwesomeIcon icon={faDesktop} />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 dark:text-slate-100">Portal Access IPs</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400">{t('ipWhitelist.portalDesc')}</p>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <UiInput
              wrapperClassName="flex-1"
              value={newPortalIP}
              onChange={(e) => setNewPortalIP(e.target.value)}
              placeholder="192.168.1.1"
            />
            <button
              onClick={() => setNewPortalIP(config.current_ip)}
              className="px-3 py-2.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-sm font-medium transition-colors shrink-0"
              title={t('ipWhitelist.useCurrentIP')}
            >
              <FontAwesomeIcon icon={faGlobe} />
            </button>
            <button
              onClick={handleAddPortalIP}
              className="px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-colors shrink-0"
              style={{ background: 'var(--theme-primary-gradient)' }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>

          {renderIpList(config.portal_ips, handleRemovePortalIP)}
        </div>

        {/* API IPs */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
              <FontAwesomeIcon icon={faPlug} />
            </div>
            <div>
              <h2 className="font-bold text-gray-800 dark:text-slate-100">API Access IPs</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400">{t('ipWhitelist.apiDesc')}</p>
            </div>
          </div>

          <div className="flex gap-2 mb-4">
            <UiInput
              wrapperClassName="flex-1"
              value={newAPIIP}
              onChange={(e) => setNewAPIIP(e.target.value)}
              placeholder="203.0.113.1"
            />
            <button
              onClick={() => setNewAPIIP(config.current_ip)}
              className="px-3 py-2.5 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-700 dark:text-slate-200 rounded-lg text-sm font-medium transition-colors shrink-0"
              title={t('ipWhitelist.useCurrentIP')}
            >
              <FontAwesomeIcon icon={faGlobe} />
            </button>
            <button
              onClick={handleAddAPIIP}
              className="px-4 py-2.5 text-white rounded-lg text-sm font-medium transition-colors shrink-0"
              style={{ background: 'var(--theme-primary-gradient)' }}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>

          {renderIpList(config.api_ips, handleRemoveAPIIP)}
        </div>
      </div>

      {/* Access Logs */}
      <DataTable<AccessLog>
        data={logs}
        columns={logColumns}
        loading={loadingLogs}
        emptyMessage={t('ipWhitelist.noLogs')}
        emptyIcon={<FontAwesomeIcon icon={faFileAlt} className="text-5xl text-gray-300 dark:text-slate-600" />}
        keyExtractor={(log) => log.id}
        pagination={{
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: false,
        }}
        headerContent={
          <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
            <FontAwesomeIcon icon={faFileAlt} className="text-amber-500" />
            {t('ipWhitelist.recentLogs')}
          </h2>
        }
        hoverable
        striped
      />
    </div>
  );
};

export default IPWhitelistPage;
